/**
 * Reine Baum- und Zähllogik der Katalogpflege (Einstellungen → Branchen-Katalog)
 * — ohne React/DOM, damit sie per Vitest prüfbar ist (wie `brancheAuswahlLogik.ts`).
 */

import type { BrancheLeaf } from "../../data/brancheClassification";

/** Eine Branchengruppe (Ebene 2) mit ihren Spezialisierungen. */
export interface KatalogGruppe {
  e2: string;
  leaves: BrancheLeaf[];
}

/** Eine Branche (Ebene 1) mit ihren Gruppen. */
export interface KatalogBranche {
  e1: string;
  groups: KatalogGruppe[];
}

/**
 * Schlüssel „Branche + Gruppe". Trennzeichen `\u0000`: mit einem normalen
 * Zeichen fielen „A B"/„C" und „A"/„B C" auf denselben Schlüssel — und „Gruppe
 * umbenennen" träfe die falsche Gruppe.
 */
export const gruppenSchluessel = (ebene1: string, ebene2: string): string =>
  `${ebene1}\u0000${ebene2}`;

/**
 * Baut Branche → Gruppe → Blatt in Katalog-Reihenfolge (`sortIndex`).
 * Deaktivierte Blätter bleiben drin — die Pflege muss sie sehen und wieder
 * aktivieren können; ausgeblendet werden sie nur in der Auswahl von Anlage/Akte.
 */
export function baueBaum(rows: BrancheLeaf[]): KatalogBranche[] {
  const baum: KatalogBranche[] = [];
  const e1Index = new Map<string, KatalogBranche>();
  const e2Index = new Map<string, KatalogGruppe>();
  const sortiert = [...rows].sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));
  for (const l of sortiert) {
    let e1 = e1Index.get(l.ebene1);
    if (!e1) {
      e1 = { e1: l.ebene1, groups: [] };
      e1Index.set(l.ebene1, e1);
      baum.push(e1);
    }
    const key = gruppenSchluessel(l.ebene1, l.ebene2);
    let e2 = e2Index.get(key);
    if (!e2) {
      e2 = { e2: l.ebene2, leaves: [] };
      e2Index.set(key, e2);
      e1.groups.push(e2);
    }
    e2.leaves.push(l);
  }
  return baum;
}

/** Wie groß eine Branche bzw. Gruppe im GESAMTEN Katalog ist. */
export interface KatalogUmfang {
  branche: (ebene1: string) => { blaetter: number; gruppen: number };
  gruppe: (ebene1: string, ebene2: string) => number;
}

/**
 * Zählt Blätter und Gruppen je Branche.
 *
 * WARUM eigens und nicht aus dem angezeigten Baum: unter aktiver Suche zeigt der
 * Baum nur die Treffer. Die Umbenennung einer Gruppe oder Branche trifft aber
 * jedes ihrer Blätter — aus dem gefilterten Baum gezählt stünde in der Warnung
 * „wirkt auf ALLE 1 Spezialisierung", während 17 umbenannt würden. Diese Zahl
 * ist das Einzige, woran der Nutzer die Tragweite vor dem Speichern erkennt.
 */
export function katalogUmfang(rows: BrancheLeaf[]): KatalogUmfang {
  const branchen = new Map<string, { blaetter: number; gruppen: Set<string> }>();
  const gruppen = new Map<string, number>();
  for (const l of rows) {
    const b = branchen.get(l.ebene1) ?? { blaetter: 0, gruppen: new Set<string>() };
    b.blaetter += 1;
    b.gruppen.add(l.ebene2);
    branchen.set(l.ebene1, b);
    const key = gruppenSchluessel(l.ebene1, l.ebene2);
    gruppen.set(key, (gruppen.get(key) ?? 0) + 1);
  }
  return {
    branche: (ebene1) => {
      const b = branchen.get(ebene1);
      return { blaetter: b?.blaetter ?? 0, gruppen: b?.gruppen.size ?? 0 };
    },
    gruppe: (ebene1, ebene2) => gruppen.get(gruppenSchluessel(ebene1, ebene2)) ?? 0,
  };
}

/** Volltextfilter über die drei Ebenen (Grundlage des gefilterten Baums). */
export function katalogFiltern(rows: BrancheLeaf[], suche: string): BrancheLeaf[] {
  const q = suche.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((l) =>
    `${l.ebene1} ${l.ebene2} ${l.ebene3}`.toLowerCase().includes(q),
  );
}
